import gql from 'graphql-tag';

const myAccountAddressFields = gql`
  fragment myAccountAddressFields on ProfileAddress {
    firstName
    lastName
    email
    phone {
      number
      phoneType
    }
    line1
    line2
    townCity
    postalCode
    country {
      isocode
    }
    region {
      isocodeShort
      isocode
    }
  }
`;

const minimalVehicleAssemblyFields = gql`
  fragment minimalVehicleAssemblyFields on VehicleAssemblyData {
    frontTireSize
    rearTireSize
    vehicleAssemblyID
    vehicle {
      year
      make
      model
    }
    trim {
      description
    }
    rideHeight {
      description
      id
      type
    }
  }
`;

export const vehicleAssemblyFields = gql`
  fragment vehicleAssemblyFields on VehicleAssemblyData {
    ...minimalVehicleAssemblyFields
    vehicleAssemblyID
    vehicle {
      id
      make
      model
      vehicleLargeImage {
        url
      }
      vehicleSmallImage {
        url
      }
      type
      year
    }
    trim {
      id
      description
      chassis {
        drive {
          driveType
          dual
          fuelType
        }
        hubData {
          lugTorque
        }
      }
      vehicleCategory
    }
    assembly {
      id
    }
    isStaggered
    frontTireAspectRatio
    rearTireAspectRatio
    frontTireSize
    rearTireSize
    frontTireWheelRimDiameter
    rearTireWheelRimDiameter
    frontWheelRimDiameter
    rearWheelRimDiameter
  }
  ${minimalVehicleAssemblyFields}
`;

const orderDataFields = gql`
  fragment orderDataFields on OrderData {
    created
    token
    appointmentData {
      date
      rescheduleURL
      changeStoreURL
      services {
        name
        code
      }
      cancellationURL
      formattedDateTime
      appointmentId
      status
    }
    deliveryAddress {
      firstName
      lastName
      phone
      email
      line1
      line2
      postalCode
      town
      region {
        isocodeShort
      }
    }
    transactionDetails {
      key
      value
    }
    refundType
    pointOfService {
      address {
        phone
        email
        line1
        line2
        postalCode
        town
        region {
          isocodeShort
        }
      }
      code
      geoPoint {
        latitude
        longitude
      }
    }
    recipientData {
      email
      firstName
      lastName
      phone
    }
    formattedDateTime
    orderType
    quoteStatus
    name
    status {
      code
      type
    }
    code
    description
    validUntilFormatted
    vehicleAssembly {
      ...minimalVehicleAssemblyFields
    }
    groupEntries {
      entryType
      entries {
        promotionData {
          code
          title
          lineItemDiscount {
            formattedValue
          }
        }
        product {
          brand
          code
          size
          productType
          primaryImageUrl
          name
          baseProduct
          rearProduct {
            code
          }
        }
        basePrice {
          formattedValue
          value
        }
        quantity
        vehicleAssembly {
          ...minimalVehicleAssemblyFields
        }
      }
    }
  }
  ${minimalVehicleAssemblyFields}
`;

export const GET_ACCOUNT_APPOINTMENTS = gql`
  query getAccountAppointments($storeCode: String!) {
    account {
      customerAppointments(storeCode: $storeCode) {
        appointmentData {
          appointmentId
          cancellationURL
          date
          formattedDateTime
          rescheduleURL
          services {
            name
          }
          status
        }
        code
        orderType
        quoteStatus
        pointOfService {
          code
          address {
            id
            line1
            town
            postalCode
            phone
            region {
              isocodeShort
            }
          }
          geoPoint {
            latitude
            longitude
          }
        }
        token
        transactionDetails {
          key
          value
        }
        status {
          code
          type
        }
        vehicleAssembly {
          ...minimalVehicleAssemblyFields
        }
      }
    }
  }
  ${minimalVehicleAssemblyFields}
`;

export const GET_ACCOUNT_ADDRESS = gql`
  query getAccountAddress($contactType: ContactEnum!) {
    account {
      address(contactType: $contactType) {
        ...myAccountAddressFields
      }
    }
  }
  ${myAccountAddressFields}
`;

const accountOrderProductFields = gql`
  fragment accountOrderProductFields on ProductData {
    brand
    code
    dtatInstall
    line
    size
    productType
    primaryImageUrl
    name
  }
`;

const accountOrderAssociatedProductFields = gql`
    fragment accountOrderAssociatedProductFields on OrderData {
      associatedProducts {
        ...accountOrderProductFields
      }
    }
  `;

export const GET_ACCOUNT_ORDER = gql`
  query getCustomerOrder($storeCode: String!, $token: String!) {
    order {
      orderByToken(storeCode: $storeCode, token: $token) {
        ...accountOrderAssociatedProductFields
        totalTax {
          currencyIso
          minQuantity
          maxQuantity
          priceType
          formattedValue
          value
        }
        subTotal {
          currencyIso
          minQuantity
          maxQuantity
          priceType
          formattedValue
          value
        }
        totalPrice {
          currencyIso
          minQuantity
          maxQuantity
          priceType
          formattedValue
          value
        }
        created
        token
        appointmentData {
          date
          rescheduleURL
          changeStoreURL
          services {
            name
            code
          }
          status
          cancellationURL
          formattedDateTime
          appointmentId
        }
        deliveryAddress {
          firstName
          lastName
          phone
          email
          line1
          line2
          postalCode
          town
          region {
            isocodeShort
          }
          comment
        }
        fleetDetails {
          companyName
          fleetAccountNumber
        }
        transactionDetails {
          key
          value
        }
        refundType
        pointOfService {
          address {
            phone
            email
            line1
            line2
            postalCode
            town
            region {
              isocodeShort
            }
          }
          applicationType
          code
          geoPoint {
            latitude
            longitude
          }
        }
        recipientData {
          email
          firstName
          lastName
          phone
        }
        formattedDateTime
        orderType
        quoteStatus
        sourceTxnType
        name
        status {
          code
          type
        }
        code
        description
        validUntil
        validUntilFormatted
        vehicleAssembly {
          ...minimalVehicleAssemblyFields
        }
        paymentInfoList {
          id
        }
        appliedOrderPromotions {
          promotionData {
            code
            lineItemDiscount {
              formattedValue
            }
          }
        }
        groupEntries {
          entryType
          supportFitment
          entries {
            adjustments {
              code
              name
              amount {
                formattedValue
              }
            }
            promotionData {
              code
              title
              lineItemDiscount {
                formattedValue
              }
            }
            product {
              ...accountOrderProductFields
            }
            basePrice {
              currencyIso
              minQuantity
              maxQuantity
              priceType
              formattedValue
              value
            }
            totalPrice {
              currencyIso
              minQuantity
              maxQuantity
              priceType
              formattedValue
              value
            }
            subtotal {
              currencyIso
              minQuantity
              maxQuantity
              priceType
              formattedValue
              value
            }
            quantity
          }
        }
      }
    }
  }
  ${minimalVehicleAssemblyFields}
  ${accountOrderProductFields}
  ${accountOrderAssociatedProductFields}
`;

export const GET_ACCOUNT_ORDERS = gql`
  query getAccountOrders($storeCode: String!) {
    account {
      customerOrders(storeCode: $storeCode) {
        pointOfService {
          applicationType
        }
        ...orderDataFields
      }
    }
  }

  ${orderDataFields}
`;

export const GET_ACCOUNT_RECENT_FITMENTS = gql`
  query getAccountRecentFitments {
    account {
      searchVehicles {
        code
        isActive
        isOE
        sizeQualifier
        frontTireSize
        rearWheelRimDiameter
        frontWheelRimDiameter
        rearTireSize
        vehicleAssembly {
          ...vehicleAssemblyFields
        }
      }
    }
  }
  ${vehicleAssemblyFields}
`;

export const GET_ACCOUNT_SAVED_FITMENTS = gql`
  query getAccountSavedFitments {
    account {
      savedVehicles {
        code
        isActive
        isOE
        sizeQualifier
        frontTireSize
        rearWheelRimDiameter
        frontWheelRimDiameter
        rearTireSize
        savedDate
        vehicleAssembly {
          ...vehicleAssemblyFields
        }
      }
    }
  }
  ${vehicleAssemblyFields}
`;

export const GET_ACCOUNT_USER = gql`
  query GetAccountProfile {
    account {
      customer {
        uid
        displayUid
        customerId
        name
        firstName
        lastName
        phone
        phoneType
        email
      }
    }
  }
`;

export const GET_ACCOUNT_UPDATE_EMAIL_CONFIRMATION = gql`
  query getAccountUpdateEmailConfirmation($email: EmailAddress!) {
    accountEmail {
      updateEmail(email: $email)
    }
  }
`;

export const GET_ACCOUNT_QUOTES = gql`
  query getAccountQuotes {
    account {
      customerQuotes {
        ...orderDataFields
      }
    }
  }

  ${orderDataFields}
`;

export const GET_ACCOUNT_VERIFICATION_BY_EMAIL = gql`
  query getAccountVerificationByEmail($email: String!) {
    account {
      findCustomerByEmail(email: $email)
    }
  }
`;
export const GET_PASSWORD_TOKEN = gql`
query checkPasswordToken($token: String!) {
  account {
    passwordResetTokenValid(token: $token)
  }
}
`;
